///Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/leads/table-components/data-table.tsx

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { PanelTopClose, PanelTopOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Papa from "papaparse";

interface LeadData {
  id: string;
  createdAt?: Date | string | null;
  profileReturning2025?: boolean | null;
  [key: string]: any;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function LeadDataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);

  
  const [hide, setHide] = React.useState(false);
  const [search, setSearch] = React.useState(""); // Add search state
  const [selectedYear, setSelectedYear] = React.useState<string>("2025"); // Add year filter state
  const [globalFilter, setGlobalFilter] = React.useState(""); // Add global filter state

  // Filter data based on selectedYear
  // Logic: If an investor is "returning" for a year, show them in that year only
  // Otherwise, show based on their createdAt date
  const filteredDataByYear = React.useMemo(() => {
    return (data as LeadData[]).filter((lead) => {
      const isReturning2025 = lead.profileReturning2025 === true;
      
      // For 2025: Show if returning for 2025 OR created in 2025
      if (selectedYear === "2025") {
        if (isReturning2025) {
          return true; // Returning investors show in 2025
        }
        // Otherwise check createdAt
        const dateField = lead.createdAt;
        if (!dateField) return true;
        try {
          const createdYear = new Date(dateField).getFullYear().toString();
          return createdYear === "2025";
        } catch (e) {
          console.error("Error parsing date for lead:", lead.id, dateField, e);
          return false;
        }
      }
      
      // For other years (2024, 2023, etc.): Show if created in that year AND NOT returning for 2025
      // This prevents returning investors from showing in both their original year and 2025
      if (isReturning2025) {
        return false; // Returning 2025 investors don't show in previous years
      }
      
      const dateField = lead.createdAt;
      if (!dateField) return true;
      try {
        const createdYear = new Date(dateField).getFullYear().toString();
        return createdYear === selectedYear;
      } catch (e) {
        console.error("Error parsing date for lead:", lead.id, dateField, e);
        return false;
      }
    });
  }, [data, selectedYear]);

  // Custom global filter function that searches across firstName and lastName
  const globalFilterFn = React.useCallback(
    (row: any, columnId: string, filterValue: string) => {
      const searchValue = filterValue.toLowerCase();
      const firstName = (row.original.firstName || "").toLowerCase();
      const lastName = (row.original.lastName || "").toLowerCase();
      
      // Match if search value is found in either firstName or lastName
      return firstName.includes(searchValue) || lastName.includes(searchValue);
    },
    []
  );

  const table = useReactTable({
    data: filteredDataByYear as TData[], // Use filtered data
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Function to handle search input and update global filter
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setGlobalFilter(value); // Use global filter to search across firstName and lastName
  };

  // Utility function to format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // Utility function to format currency
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || isNaN(amount)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Mapping function for export
  const mapDataForExport = (data: any[]) => {
    return data.map((row) => ({
      "Full Name": `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      "E-mail": row.email || '',
      "Profile Progress": row.formProgress || '',
      "Amount": formatCurrency(row.investmentAmount),
      "Date": formatDate(row.createdAt)
    }));
  };

  // CSV Export Function
  const handleExport = () => {
    // Map and format the data
    const exportData = mapDataForExport(data);

    // Define CSV headers
    const csvHeaders = ["Full Name", "E-mail", "Profile Progress", "Amount", "Date"];

    // Convert to CSV
    const csv = Papa.unparse({
      fields: csvHeaders,
      data: exportData,
    });

    // Create a blob and trigger download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "leads.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <Input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by first name or last name..."
            className="max-w-sm h-9"
          />
          {/* Year Filter Select */}
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2023">2023</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          {/* Export Button */}
          <Button onClick={handleExport} variant="secondary" size="sm">
            Export CSV
          </Button>

          {/* Hide/Show Toggle */}
          {hide ? (
            <Button variant="ghost" size="icon" onClick={() => setHide(false)} title="Show Table">
              <PanelTopOpen className="w-5 h-5 text-muted-foreground" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => setHide(true)} title="Hide Table">
              <PanelTopClose className="w-5 h-5 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {hide ? (
        <div className="flex gap-2">
          This content is hidden now. Click on <PanelTopOpen /> to show content
        </div>
      ) : (
        <>
          <DataTableToolbar table={table} />
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination table={table} />
        </>
      )}
    </div>
  );
}